import * as React from 'react';
import type { ExecutionResult, FetchResult } from 'react-apollo';
import { Mutation } from 'react-apollo';

import type { DataProxy } from 'apollo-cache';

import batchRubricUpdateMutation from 'bundles/grading/api/BatchUpdateReviewPartResponsesMutation.graphql';
import submitGradesMutation from 'bundles/grading/api/SubmitGradesToGradeBookMutation.graphql';
import type {
  BatchUpdateReviewPartResponsesMutation,
  BatchUpdateReviewPartResponsesMutationVariables,
  SubmissionDataQueryVariables,
  SubmitGradesToGradeBookMutation,
  SubmitGradesToGradeBookMutationVariables,
  SubmitterListQueryVariables,
} from 'bundles/grading/api/types';
import { ItemType } from 'bundles/grading/api/types';
import type { FetchStatus } from 'bundles/grading/components/staff-graded-assignments/types/SGAGradingTypes';
import { QUERY_LIMIT } from 'bundles/grading/hooks/useSubmitterListQuery';
import { updateCachedSubmissionData, updateCachedSubmitterGradingStatus } from 'bundles/grading/utils/apiUtils';

export type BatchRubricUpdateFunction = (param: {
  variables: BatchUpdateReviewPartResponsesMutationVariables;
}) => Promise<void | FetchResult<BatchUpdateReviewPartResponsesMutation, Record<string, $TSFixMe>>>;

export type SubmitGradesFunction = (param: {
  variables: SubmitGradesToGradeBookMutationVariables;
}) => Promise<void | FetchResult<SubmitGradesToGradeBookMutation, Record<string, $TSFixMe>>>;

export type BatchUpdateAndSubmitGradesMutationInjectedProps = {
  batchRubricUpdate: BatchRubricUpdateFunction;
  batchRubricUpdateStatus: FetchStatus;
  submitGrades: SubmitGradesFunction;
  submitGradesStatus: FetchStatus;
};

type Props = {
  children: (mutations: BatchUpdateAndSubmitGradesMutationInjectedProps) => React.ReactElement | null;
};

/**
 * Feature mutation wrapper handling:
 *  - BatchUpdateReviewPartResponsesMutation mutation
 *  = SubmitGradesToGradeBookMutation mutation
 */
const BatchUpdateAndSubmitGradesMutationWrapper: React.FC<Props> = ({ children }) => {
  /**
   * Handle updates on mutation complete
   * Updates the cached submission and submitterList with given sgaSubmission response
   *
   * @param { DataProxy } cache
   * @param { ExecutionResult<BatchUpdateReviewPartResponses | SubmitGradesToGradeBookMutation> } response
   * @param { SubmitGradesToGradeBookGradebookEntry? } gradebookEntry
   */
  const updateCachedSubmissionAndSubmitterList = (
    cache: DataProxy,
    response: ExecutionResult<BatchUpdateReviewPartResponsesMutation | SubmitGradesToGradeBookMutation>
  ) => {
    if (!response.data) {
      return;
    }
    const data =
      (response.data as BatchUpdateReviewPartResponsesMutation).GroupGrading_BatchUpdateReviewPartResponses ||
      (response.data as SubmitGradesToGradeBookMutation).GroupGrading_SubmitGradesToGradeBook;
    const sgaSubmission = data.sgaSubmission;
    const gradeBookEntry = (response.data as SubmitGradesToGradeBookMutation).GroupGrading_SubmitGradesToGradeBook
      ?.gradebookEntry;

    const itemFilter = {
      itemIds: [data.submissionReference.itemId],
      itemTypes: [ItemType.StaffGradedAssignment],
    };

    /**
     * Apply post rubric-save and post grade-submit mutation actions for submissionData cached data
     */
    const submissionQueryVariables: SubmissionDataQueryVariables = {
      id: data.submissionReference.groupId,
      itemFilter,
      gradingTaskFilter: {
        submitterIds: [data.submissionReference.submitterId],
      },
    };

    updateCachedSubmissionData(cache, sgaSubmission, submissionQueryVariables, gradeBookEntry);

    /**
     * Apply post grade-submit mutations for submitterList cached data
     */
    const submitterQueryVariables: SubmitterListQueryVariables = {
      id: data.submissionReference.groupId,
      itemFilter,
      limit: QUERY_LIMIT,
    };
    updateCachedSubmitterGradingStatus(
      cache,
      data.sgaSubmission,
      submitterQueryVariables,
      data.submissionReference.submitterId
    );
  };

  return (
    <Mutation<BatchUpdateReviewPartResponsesMutation, BatchUpdateReviewPartResponsesMutationVariables>
      mutation={batchRubricUpdateMutation}
      context={{ clientName: 'gatewayGql' }}
      update={updateCachedSubmissionAndSubmitterList}
    >
      {(batchRubricUpdate, batchRubricUpdateResult) => (
        <Mutation<SubmitGradesToGradeBookMutation, SubmitGradesToGradeBookMutationVariables>
          mutation={submitGradesMutation}
          context={{ clientName: 'gatewayGql' }}
          update={updateCachedSubmissionAndSubmitterList}
        >
          {(submitGrades, submitGradesResult) => {
            const batchRubricUpdateStatus: FetchStatus = {
              loading: batchRubricUpdateResult.loading,
              error: batchRubricUpdateResult.error,
            };
            const submitGradesStatus: FetchStatus = {
              loading: submitGradesResult.loading,
              error: submitGradesResult.error,
            };
            const batchUpdateAndSubmitGradesMutations = {
              batchRubricUpdate,
              batchRubricUpdateStatus,
              submitGrades,
              submitGradesStatus,
            };
            return children(batchUpdateAndSubmitGradesMutations);
          }}
        </Mutation>
      )}
    </Mutation>
  );
};

export default BatchUpdateAndSubmitGradesMutationWrapper;
